/**
 * THE FILE — the instruction line and the drop target.
 *
 * These two are where work enters the product, and they are deliberately not
 * a form. An accountant states the job in their own words and encloses the
 * records; Leveri reads both and asks only for what neither establishes. A
 * form would ask which client, which engagement, which period and which
 * currency BEFORE reading the sentence — four questions the product should be
 * answering rather than setting.
 */
import { clsx } from "clsx";
import { ArrowUp, Plus } from "lucide-react";
import type { DragEventHandler, ReactNode } from "react";

export interface InstructionLineProps {
  /** Opens the file picker. The records are enclosed WITH the instruction. */
  onEnclose?: (() => void) | undefined;
  /** Dictation, supplied only where the browser actually provides it. */
  dictate?: ReactNode;
  /** The writing area itself — the screen owns the field, this owns the line. */
  children: ReactNode;
  onSend?: (() => void) | undefined;
  sendDisabled?: boolean | undefined;
  sendLabel?: string | undefined;
  className?: string | undefined;
}

/**
 * THE INSTRUCTION LINE — the product's signature.
 *
 * A 2px ink rule under the sentence, the enclose button on the left, send on
 * the right. It is a LINE TO WRITE ON, not a box to fill in: a bordered
 * textarea with a placeholder reads as a field on a form, and a field on a
 * form invites a short answer. A ruled line invites a sentence, which is what
 * the engine reads.
 *
 * The send button is the one saturated element on this surface. Disabled it
 * goes to the disabled-primary fill rather than losing its colour entirely, so
 * the eye still knows where the action is while it waits for a reason to be
 * allowed.
 */
export function InstructionLine({
  onEnclose,
  dictate,
  children,
  onSend,
  sendDisabled,
  sendLabel = "Give Leveri the work",
  className,
}: InstructionLineProps) {
  return (
    <div className={clsx("flex items-end gap-3 border-b-2 border-rule-strong pb-3 pt-2", className)}>
      {/* A BUTTON THAT DOES NOTHING IS WORSE THAN NO BUTTON. Records are
          enclosed into a piece of WORK, so there is nowhere to put one before
          the work exists — and on that surface the affordance simply is not
          drawn, rather than drawn and inert. */}
      {onEnclose && (
        <button
          type="button"
          aria-label="Enclose records"
          onClick={onEnclose}
          className="flex h-[34px] w-[34px] shrink-0 items-center justify-center rounded-control border border-ink text-ink hover:bg-band"
        >
          <Plus size={16} />
        </button>
      )}
      <div className="min-w-0 flex-1">{children}</div>
      {dictate}
      <button
        type="button"
        aria-label={sendLabel}
        onClick={onSend}
        disabled={sendDisabled}
        className="flex h-9 w-9 shrink-0 items-center justify-center rounded-control bg-forest text-forest-on hover:bg-forest-hover disabled:cursor-not-allowed disabled:bg-brand-line"
      >
        <ArrowUp size={16} />
      </button>
    </div>
  );
}

export interface DropTargetProps {
  /** True while a drag is over the target. The only state it has. */
  dragging?: boolean | undefined;
  onDragOver?: DragEventHandler<HTMLDivElement> | undefined;
  onDragLeave?: DragEventHandler<HTMLDivElement> | undefined;
  onDrop?: DragEventHandler<HTMLDivElement> | undefined;
  children: ReactNode;
  className?: string | undefined;
}

/**
 * The dashed target for records: at rest, under a drag, and always with a
 * sentence in it.
 *
 * Dashed rather than solid because a dashed edge reads as "something goes
 * here" while a solid one reads as "something is here". Under a drag the edge
 * goes to forest — the only moment the brand colour is used to confirm a
 * gesture rather than to mark an action.
 */
export function DropTarget({ dragging, onDragOver, onDragLeave, onDrop, children, className }: DropTargetProps) {
  return (
    <div
      onDragOver={onDragOver}
      onDragLeave={onDragLeave}
      onDrop={onDrop}
      className={clsx(
        "flex items-center gap-3 border-[1.5px] border-dashed px-5 py-4 text-[13px] text-stone transition-colors duration-150",
        dragging ? "border-forest" : "border-rule-dashed",
        className
      )}
    >
      {children}
    </div>
  );
}
